'use client';
import React from "react";
import { useState, useEffect } from "react";
import { observer } from "mobx-react";
import Delete from "./assets/icon_reduce_delete.svg";
import Add from "./assets/icon_add.svg";
import Reduce from "./assets/icon_reduce.svg";
import styles from "./index.module.scss";

interface CounterProps {
  value: number;
  minAmount: number;
  maxAmount: number;
  stock?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  immediately?: boolean;
  size?: string; // 目前暂时支持small、middle(购物车列表可见)
  isSetDefaultValue?: boolean;
  setIsCancelDelete?: (val: boolean) => void;
  onCounterType?: (counterType: string) => void; // 计数器类型 + - input，可用于埋点
  isShowDeleteIcon?: boolean; // 315购物车需求新增，支持展示删除图标
  isClickService?: boolean; // v730 打开大件规格服务，禁止改变及增加数量
  onClickService?: () => void; // v730 打开大件规格服务，点击服务
}

interface MyState {
  value: number;
  reduceDisable: boolean;
  addDisable: boolean;
}

export enum CounterType {
  reduce = "reduce",
  add = "add",
  input = "input",
}

export const debounce = <T extends (...args: unknown[]) => void>(func: T, wait: number, immediate?: boolean) => {
  let timeout: NodeJS.Timeout | null = null;

  return function (this: unknown, ...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) {
        func.apply(this, args);
      }
    }, wait);

    if (immediate && !timeout) {
      func.apply(this, args);
    }
  };
};

const UKCounter: React.FC<CounterProps> = observer(
  ({
    value,
    minAmount,
    maxAmount,
    stock,
    disabled = false,
    onChange,
    immediately = true,
    size,
    isSetDefaultValue,
    setIsCancelDelete,
    onCounterType,
    isShowDeleteIcon,
    isClickService = false,
    onClickService,
  }) => {
    const [state, setState] = useState<MyState>({
      value,
      reduceDisable: false,
      addDisable: false,
    });
    const [isInput, setInput] = useState(false); // 默认没获取焦点

    useEffect(() => {
      if (value) {
        setState((prevState) => ({
          ...prevState,
          value: value,
        }));
      }
      if (isSetDefaultValue) {
        setState((prevState) => ({
          ...prevState,
          value: 1,
        }));
        if (setIsCancelDelete) {
          setIsCancelDelete(false);
        }
      }
    }, [value, isSetDefaultValue]); // 依赖于 state.value

    const maxLocal = stock ? Math.min(stock, maxAmount) : maxAmount;

    useEffect(() => {
      // if (state.value!== value) {
      //   computeCounter();
      // }
      if (!isInput) {
        computeCounter();
      }
    }, [state.value, isInput]); // 依赖于 state.value

    const computeCounter = () => {
      let localValue = state.value;
      if (state.value >= maxLocal) {
        setState((prevState) => ({
          ...prevState,
          addDisable: true,
        }));
        localValue = maxLocal;
        setState((prevState) => ({
          ...prevState,
          value: localValue,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          addDisable: false,
        }));
      }
      if (state.value <= minAmount) {
        setState((prevState) => ({
          ...prevState,
          reduceDisable: true,
        }));
        localValue = minAmount;
        setState((prevState) => ({
          ...prevState,
          value: localValue,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          reduceDisable: false,
        }));
      }
      if (state.value !== value) {
        onChange(state.value);
      }
      // onChange(state.value);
    };

    const update = debounce(() => {
      computeCounter();
    }, 500);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInput(true);
      const val = parseInt(e.target.value);
      // if (isNaN(val)) {
      //    val = minAmount;
      // }
      setState((prevState) => ({
        ...prevState,
        value: val,
      }));
      onCounterType && onCounterType(CounterType.input);
      // update();
    };

    const handleReduce = () => {
      setInput(false);
      if (state.reduceDisable || disabled) {
        return;
      }
      setState((prevState) => ({
        ...prevState,
        value: Number(prevState.value) - 1,
      }));
      onCounterType && onCounterType(CounterType.reduce);
    };

    const handleAdd = () => {
      if (isClickService) {
        return onClickService?.();
      }
      setInput(false);
      if (state.addDisable || disabled) {
        return;
      }
      setState((prevState) => ({
        ...prevState,
        value: Number(prevState.value) + 1,
      }));
      onCounterType && onCounterType(CounterType.add);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isClickService) {
        return;
      }
      setInput(false);
      let val = e.target.value;
      // 去掉前导零
      if (val.startsWith('0') && val.length > 1) {
        val = val.replace(/^0+/, ''); // 使用正则表达式去掉前导零
      }
      setState((prevState) => ({
        ...prevState,
        value: Number(val),
      }));
      // 手动更新输入框的值
      e.target.value = val.toString();
      // if (!val) {
      //   // setState((prevState) => ({
      //   //  ...prevState,
      //   //   value: 1,
      //   // }));
      //   // onChange(state.value);
      //   setState((prevState) => ({
      //     ...prevState,
      //     value: minAmount, // 设置为最小值
      //   }));
      //   // onChange(minAmount); // 直接传递最小值
      // }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // 阻止输入小数点
      if (e.key === '.') {
        e.preventDefault();
      }
    };

    // 新增：处理输入框点击事件
    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
      if (isClickService) {
        e.preventDefault();
        e.stopPropagation();
        return onClickService?.();
      }
    };

    return (
      <div className={`${styles.counter} ${styles["counter-row"]} ${size ? styles[`${size}-counter`] : ""}`}>
        <i
          className={`${styles["counter-input-icon"]} ${styles["counter-reduce"]}
          ${state.reduceDisable || disabled ? styles.disabled : ""}`}
          style={{
            color: state.reduceDisable || disabled ? "#C2C4CC" : "#1A1A1A",
            cursor: state.reduceDisable || disabled ? "not-allowed" : "pointer"
          }}
          onClick={debounce(handleReduce, 300)}
        >{isShowDeleteIcon && state.value === 1 ? <Delete /> : <Reduce />}</i>
        <input
          type="number"
          step={1}
          onKeyDown={handleKeyDown} // 添加键盘事件处理
          value={state.value}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={isClickService}
          onClick={handleInputClick}
          onChange={handleChange} />
        <i
          className={`${styles["counter-input-icon"]} ${styles["counter-add"]}
          ${state.addDisable || disabled ? styles.disabled : ""}`}
          style={{
            color: state.addDisable || disabled ? "#C2C4CC" : "#1A1A1A",
            cursor: state.addDisable || disabled ? "not-allowed" : "pointer"
          }}
          onClick={debounce(handleAdd, 300)}
        ><Add /></i>
      </div>
    );
  },
);

export default UKCounter;
